import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  getBrief,
  getBriefRecipientsState,
  loadBrief,
  loadBriefRecipients,
} from '@disco/state-management/reducers/entities/briefs'
import DocumentHeadTags from 'components/document-head-tags'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { ManageAccessModal } from '../../components/manage-access-modal'
import { Spinner } from 'components/ds'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { useReopenBrief } from '../../hooks/use-reopen-brief'
import { updateBriefInPanes } from '@disco/state-management/actions/ExplorerActions'
import mixpanel from '@disco/api/mixpanel'
import dayjs from '@disco/utils/dayjs'

const BriefManageAccess: React.FC = () => {
  const dispatch = useDispatch()
  const params = useParams<{ briefId: string }>()
  const location = useLocation()
  const returnPathRef = useRef(location?.state?.from)
  const navigate = useNavigate()

  const [isShareModalOpen, setIsShareModalOpen] = useState(true)
  const [shareModalInitialRecipients, setShareModalInitialRecipients] =
    useState<string[]>([])

  const briefId = parseInt(params.briefId)

  const brief = useSelector(state => getBrief(state, { briefId }))

  const sharePath = location.pathname.includes('brief-editor-v2')
    ? `/brief-editor-v2/${brief?.id}/share`
    : `/briefs/${brief?.id}/share`

  const basePath = location.pathname.includes('brief-editor-v2')
    ? `/brief-editor-v2/${brief?.id}`
    : '/briefs'

  const handleCloseModal = useCallback(() => {
    if (returnPathRef.current) {
      navigate(returnPathRef.current)
      return
    }
    navigate(basePath)
  }, [navigate, basePath])

  const { isDeadlineInPast, handleReopenBrief, deadlinePickerModal } =
    useReopenBrief({
      brief,
      onSuccess: updatedBrief => {
        const initialDeadline = dayjs(brief.deadline)
        const newDeadline = dayjs(updatedBrief.deadline)
        const number_days_extended = initialDeadline.diff(newDeadline, 'day')
        mixpanel.sendPureEvent('Brief reopened', {
          Context: 'Briefs',
          brief_id: updatedBrief.id,
          deadline_extended: updatedBrief.deadline,
          days_extended: number_days_extended,
        })
        dispatch(updateBriefInPanes(updatedBrief))
        navigate(sharePath, {
          state: { initialRecipients: shareModalInitialRecipients },
        })
        setShareModalInitialRecipients([])
      },
      onCancel: () => {
        handleCloseModal()
        setIsShareModalOpen(true)
      },
    })

  useEffect(() => {
    if (!brief?.id) {
      dispatch(loadBrief(briefId))
    }
    dispatch(loadBriefRecipients(briefId))
  }, [dispatch, briefId, brief?.id])

  const { loading, data: briefRecipients } = useSelector(state =>
    getBriefRecipientsState(state, { briefId }),
  )

  const handleShareWithRecipients = (emails: string[]) => {
    if (isDeadlineInPast) {
      setIsShareModalOpen(false)
      setShareModalInitialRecipients(emails)
      handleReopenBrief()
    } else {
      navigate(sharePath, {
        state: { initialRecipients: emails },
      })
    }
  }

  if (!brief) return <Spinner />

  return (
    <div>
      <DocumentHeadTags title="Manage Brief Access | DISCO" />
      {isShareModalOpen && (
        <ManageAccessModal
          loading={loading}
          onClose={handleCloseModal}
          brief={brief}
          briefRecipients={briefRecipients}
          onShareWithRecipients={handleShareWithRecipients}
        />
      )}
      {deadlinePickerModal}
    </div>
  )
}

export { BriefManageAccess }
export default BriefManageAccess
